// Chip amounts are shown either as chips or as big blinds, never both at once. The choice is
// a per-user preference stored on the server so it follows the player between devices.
import { useQueryClient } from '@tanstack/react-query';
import { chips, localeTag } from '../i18n/index.ts';
import { api } from './api.ts';
import { useSession } from './session.tsx';

export type AmountUnit = 'chips' | 'bb';

export function formatAmount(n: number, unit: AmountUnit, bb: number): string {
  if (unit === 'bb' && bb > 0) {
    const v = Math.round((n / bb) * 10) / 10;
    return `${v.toLocaleString(localeTag)} BB`;
  }
  return chips(n);
}

export type AmountFormatter = (n: number) => string;

export function makeFormatter(unit: AmountUnit, bb: number): AmountFormatter {
  return (n) => formatAmount(n, unit, bb);
}

export function useAmountUnit(): [AmountUnit, (unit: AmountUnit) => Promise<void>] {
  const { me } = useSession();
  const qc = useQueryClient();
  const unit: AmountUnit = me?.uiPrefs?.amountUnit === 'bb' ? 'bb' : 'chips';
  const set = async (next: AmountUnit) => {
    // Optimistic: the display switches immediately; the server stores the preference.
    qc.setQueryData(['me'], (old: typeof me) => (old ? { ...old, uiPrefs: { ...old.uiPrefs, amountUnit: next } } : old));
    try {
      await api.patch('/api/me', { uiPrefs: { amountUnit: next } });
    } catch {
      // not saved: the refetch below shows the stored unit again
    } finally {
      // On failure this brings back the saved unit, so the screen never shows an unsaved choice.
      await qc.invalidateQueries({ queryKey: ['me'] });
    }
  };
  return [unit, set];
}
